/**
 * Documents API Endpoints
 *
 * User-facing document management: upload, list, view, delete.
 * Admin approval workflow is in admin.ts.
 */

import { api, apiBaseUrl, createLogger, randomUUID } from '@ai4nr/tricorder-ui'
import type { Page } from './types'

const logger = createLogger('DocumentsAPI');

// Document types match backend knowledge_service.proto and api-service-go handlers
export type DocumentStatus =
  | 'processing'
  | 'ready'
  | 'error'
  | 'partial_error'
  | 'quarantined'
  | 'pending_approval'
  | 'superseded';

// Categories are config-driven (app.yaml knowledge_base.categories).
// This type is intentionally a string — no hardcoded enum.
export type DocumentCategory = string;

// 'private' = owner only; 'department' = shared with one org-unit/location
// (scope_target carries its id; backend writes a node-share VIEWER grant);
// 'public' = whole organisation (user:* wildcard grant).
export type DocumentScope = 'public' | 'private' | 'department';

/**
 * One open audience-expansion request on a document — kind 'visibility'
 * (target_scope set) or 'share' (principal_type + principal_name set).
 * Mirrors tricorder-go/apicontract PendingPublish.
 */
export interface PendingPublish {
  request_id: string;
  kind: 'visibility' | 'share';
  target_scope?: string;
  principal_type?: string;
  principal_name?: string;
}

/**
 * ACL-derived visibility of a document — the actual grant set, not the legacy
 * `scope` label. `org_wide` = a group:public grant (everyone in the tenant can
 * read it); `roles` = the role names the document is shared with; `units` =
 * the org-unit names whose working set holds a grant (node shares). A document
 * with none of the three is private (owner + explicit per-user shares only).
 * `pending_publishes` carries the OPEN audience-expansion requests awaiting
 * approval, so the owner sees "pending approval" instead of a silently-
 * unchanged state. All arrays are always present (non-nil on the wire).
 * Mirrors tricorder-go/apicontract DocumentVisibility.
 */
export interface DocumentVisibility {
  org_wide: boolean;
  roles: string[];
  units: string[];
  pending_publishes: PendingPublish[];
}

/**
 * Statuses a user can retry. A failed ingestion (error / partial_error) can be
 * re-run in place. `quarantined` is deliberately excluded: it is a SAFETY hold
 * (an injection pattern was detected in the document text), not a transient
 * failure — re-running ingestion would hit the same quarantine. Mirrors the
 * knowledge-service guard in Service.ReprocessDocument.
 */
export const RETRYABLE_STATUSES: ReadonlySet<DocumentStatus> = new Set<DocumentStatus>([
  'error',
  'partial_error',
]);

export interface Document {
  id: string;
  owner_id: string;
  // Owner's display name (api-service enrichment from the users table) — used
  // to attribute rows that are not yours ("by Maria"). Absent when the owner
  // set no display name. Mirrors apicontract DocumentResponse.OwnerName.
  owner_name?: string;
  source_type: string;
  source_id: string;
  title: string;
  filename: string;
  mime_type: string;
  category: DocumentCategory;
  scope: DocumentScope;
  scope_target: string; // org-unit/location id when scope='department', else empty
  gcs_path: string;
  chunk_count: number;
  // Count of this document's chunks whose embedding model differs from the
  // knowledge service's currently configured model — the read path for
  // "which document does this embedding_model_mismatch warning mean".
  // Always present: the producing struct is tricorder-go/apicontract's
  // DocumentResponse, whose StaleChunkCount carries `json:"stale_chunk_count"`
  // with no omitempty, so 0 goes on the wire as 0 rather than vanishing.
  // 0 on a fully-current document. No UI reads this yet.
  stale_chunk_count: number;
  status: DocumentStatus;
  error_message: string;
  superseded_by: string;
  // Always on the wire and never null: `{}` is the answer "this document
  // carries no metadata", which is a different fact from a projection that
  // dropped it. The Go side guarantees the object (nonNilMap) because a nil map
  // marshals to `null` whatever the struct tag says — which is what this
  // non-nullable declaration was quietly wrong about until
  // TestDocumentMetadata_EmptyIsAnObjectNotNull pinned the bytes.
  metadata: Record<string, string>;
  created_at: string;
  updated_at: string;
  // ACL-derived truth of who can read this document — render visibility from
  // THIS, not `scope` (which carries dead values like 'institution' with no
  // grant behind them). Always present from api-service; `roles` is never null.
  visibility: DocumentVisibility;
  // viewer | editor | owner | unspecified — from ACL. The 'unspecified'
  // variant fires when api-service-go's middleware sees Allowed=true
  // with Role_UNSPECIFIED (a contract violation surfaced on the wire).
  // UI that branches on this field must treat 'unspecified' as a
  // permission-less / read-only fallback, not as 'viewer'.
  effective_role?: 'owner' | 'editor' | 'viewer';
  // Pointer to this document's bytes — the /documents/{id}/content
  // sub-resource, published by the server so no client string-builds the URL.
  // Absent when the document has neither stored bytes nor an http(s) source,
  // which is the same fact as "there is nothing to download".
  content?: ContentRef;
}

/**
 * Locator for a document's bytes. Mirrors apicontract.ContentRef.
 *
 * `href` is an API path ("/api/v1/documents/<id>/content"), not an absolute
 * URL: the API may live on another origin, so resolve it with
 * `getDocumentContentURL` rather than using it directly in an <a href>.
 *
 * `type` and `etag` describe bytes this backend holds; both are absent for a
 * URL-ingested document, whose content route redirects to the origin, and
 * `etag` is also absent before the document has a ready version. There is no
 * size here — a HEAD on `href` answers that from the stored object, which is
 * the only number that cannot disagree with the Content-Length of a GET.
 */
export interface ContentRef {
  href: string;
  type?: string;
  etag?: string;
}

/**
 * Result shape from `listDocuments`. The wire format is NDJSON: one
 * `Document` per line, '\n'-terminated; the total-matching-count rides
 * in the `X-Total-Count` response header (and is `null` when the header
 * is absent or non-numeric).
 */
export interface DocumentListResult {
  documents: Document[];
  totalCount: number | null;
}

export interface DocumentStatusResponse {
  status: DocumentStatus;
  chunks_processed: number;
  error_message: string;
}

/**
 * One structure-aware chunk of a document — a "component" in the Protocol
 * Studio's left pane (CUJ-102). Mirrors api-service-go DocumentChunkResponse.
 */
export interface DocumentChunk {
  id: string;
  content: string;
  chunk_index: number;
  section?: string;
  title?: string;
  page_start?: number;
  page_end?: number;
  // Embedding model tag this chunk was embedded with. Absent when empty
  // (api-service-go DocumentChunkResponse.EmbeddingModel has omitempty) —
  // e.g. a chunk ingested before this field existed. No UI reads this yet.
  embedding_model?: string;
}

/**
 * Ordered chunk list for one document — the one list envelope plus the facts
 * about the DOCUMENT the chunks came from. Mirrors DocumentChunksResponse.
 *
 * This read has no window, so the response carries no `Link: rel="next"`.
 */
export interface DocumentChunksResponse extends Page<DocumentChunk> {
  document_id: string;
  title?: string;
  source_type?: string;
}


// Discriminated union: file upload or URL ingestion — both POST /documents.
// Backend dispatches on Content-Type (multipart/form-data vs application/json).
export interface FileUploadInput {
  kind: 'file';
  file: File;
  title: string;
  category: DocumentCategory;
  scope: DocumentScope;
  scopeTarget?: string; // Required when scope='department' — the org-unit/location id to share with
}

export interface URLIngestInput {
  kind: 'url';
  url: string;
  category?: DocumentCategory;
  scope?: DocumentScope;
  scopeTarget?: string; // Required when scope='department' — the org-unit/location id to share with
  metadata?: Record<string, string>;
}

export type CreateDocumentRequest = FileUploadInput | URLIngestInput;

export interface CreateDocumentResponse {
  document_id: string;
  title: string;
  status: DocumentStatus;
  url?: string;
  chunk_count?: number;
  metadata?: Record<string, string>;
}

// A document title may not carry XSS-prone characters. & and ' are allowed
// (React auto-escapes in JSX). Every surface that names a new document
// (the upload page, the Studio's written source) checks the same rule.
const INVALID_TITLE_CHARS = /[<>"]/;

export function hasInvalidTitleChars(title: string): boolean {
  return INVALID_TITLE_CHARS.test(title);
}

/**
 * Create a document — POST /documents.
 * Sends multipart/form-data for file uploads, application/json for URL ingestion.
 * Both paths include an Idempotency-Key header.
 */
export async function createDocument(request: CreateDocumentRequest): Promise<CreateDocumentResponse> {
  // Generated per call — callers needing retry-with-same-key must hold the key externally.
  const idempotencyKey = randomUUID();
  const headers = { 'Idempotency-Key': idempotencyKey };

  if (request.kind === 'url') {
    logger.debug('Ingesting URL', { url: request.url, scope: request.scope });
    const response = await api.post<CreateDocumentResponse>('/documents', {
      url: request.url,
      category: request.category,
      scope: request.scope ?? 'private',
      scope_target: request.scopeTarget,
      metadata: request.metadata,
    }, { headers });
    logger.info('URL ingested', { id: response.document_id, title: response.title, status: response.status });
    return response;
  }

  logger.debug('Uploading file', {
    title: request.title,
    category: request.category,
    scope: request.scope,
    filename: request.file.name,
    size: request.file.size,
  });

  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('title', request.title);
  formData.append('category', request.category);
  formData.append('scope', request.scope);
  if (request.scopeTarget) {
    formData.append('scope_target', request.scopeTarget);
  }

  const response = await api.post<CreateDocumentResponse>('/documents', formData, { headers });
  logger.info('File uploaded', { id: response.document_id, status: response.status });
  return response;
}


/** One matching chunk within a document (passages=N>0). Mirrors api-service-go PassageResult. */
export interface PassageResult {
  snippet: string;
  score: number;
}

export interface DocumentSearchResult {
  document_id: string;
  title: string;
  category: string;
  snippet: string;
  score: number;
  // Present only when the request carried passages=N>0 (WS-B); this
  // document's top-N matching chunks, descending by relevance — entry 0 is
  // always the same chunk as snippet/score. Mirrors api-service-go
  // DocumentSearchResult.Passages (omitempty on the wire).
  passages?: PassageResult[];
}

/**
 * One published skill label that matched the query. Mirrors api-service-go
 * MatchedExpertiseResult.
 *
 * `source` is the KIND OF RECORD behind the skill, not a provenance label of
 * its own: api-service-go strongestBasis picks the highest-weighted entry of
 * the fold's `basis` and sends that word, so the vocabulary here is the basis
 * vocabulary. It replaces the three legacy strings self_declared /
 * peer_endorsement / ai_session_inference, which nothing produces any more.
 *
 * `proposal` and `target` are deliberately absent: neither is a basis, so
 * neither can reach the index. The union is REPLACED rather than widened —
 * keeping the legacy strings beside these would type a value nothing can send
 * and leave every reader a branch no data reaches.
 */
export interface MatchedExpertiseResult {
  label: string;
  source: 'declaration' | 'endorsement' | 'qualification' | 'attempt';
  strength: number;
}

/**
 * One colleague whose PUBLISHED expertise matched the query, alongside
 * (never instead of) document results (spec expert-discovery.md §4). A
 * match stub only — never live availability (that's GET /me/colleagues).
 * Mirrors api-service-go PersonMatchResult.
 */
export interface PersonMatchResult {
  user_id: string;
  name: string;
  role: string;
  specialisation: string;
  matched: MatchedExpertiseResult[];
  relevance_score: number;
  rank: number;
}

/**
 * One short, decisive statement (or a fact read from a figure the text
 * layer cannot see) matched by the search's lexical lookup leg (2026-07-30
 * structured-extracts generalisation), alongside (never instead of)
 * `results` — a matched extract's document is present here even when it
 * did not otherwise rank in `results`. `text` is VERBATIM from the source
 * document — render it unmodified, never paraphrased or unit-converted.
 * `kind` is config-driven data (e.g. "dose"), never a hardcoded enum.
 * Mirrors api-service-go ExtractResult.
 */
export interface ExtractResult {
  document_id: string;
  document_title: string;
  subject: string;
  kind: string;
  branch_label: string;
  text: string;
  section: string;
  page_start: number;
  page_end: number;
  source_kind: 'text-algorithm' | 'text-table' | 'figure';
  terms: Record<string, string>; // lang -> display line
}

/**
 * One document holding content this pipeline could not read — MUST be
 * surfaced explicitly; silence is the failure mode. Mirrors api-service-go
 * UnreadableSourceResult.
 */
export interface UnreadableSourceResult {
  document_id: string;
  document_title: string;
  pages: number[]; // 1-based pages that could not be read
}

export interface DocumentSearchResponse {
  results: DocumentSearchResult[];
  // Expert-discovery matches alongside the documents (spec §4); [] never
  // null. Mirrors api-service-go DocumentSearchResponse.People.
  people: PersonMatchResult[];
  // Short, decisive statements matched alongside the documents (2026-07-30
  // structured-extracts generalisation); [] never null.
  extracts: ExtractResult[];
  // Documents holding content this pipeline could not read; [] never null.
  unreadable_sources: UnreadableSourceResult[];
}

/**
 * Content-aware search over the knowledge base. Hits chunk content via the
 * backend hybrid (BM25 + vector) search, aggregated to one row per document.
 * Documents still in `processing` are not yet indexed and will not appear.
 */
export async function searchDocuments(q: string, category?: string): Promise<DocumentSearchResponse> {
  const params = new URLSearchParams({ q });
  if (category) params.set('category', category);
  const response = await api.get<DocumentSearchResponse>(`/documents?${params.toString()}`);
  logger.info('Documents searched', { q, count: response.results.length });
  return response;
}

/**
 * Absolute URL for a document's bytes, resolved from the server-published
 * `content.href`.
 *
 * The path is NOT rebuilt here. The document carries its own content locator,
 * so a route change on the server reaches this client without a matching
 * frontend edit — the reason the link is in the payload at all. `href` is an
 * absolute path, so resolving it against the API base replaces that base's
 * path instead of appending to it, and `/api/v1` cannot appear twice.
 *
 * `apiBaseUrl()` is same-origin-relative ("/api/v1") by default and absolute when
 * VITE_API_URL points elsewhere, which is why it is itself resolved against
 * the page origin first — `new URL(href, "/api/v1")` throws.
 *
 * The route serves the bytes inline by default, so the returned URL renders in
 * an iframe or embed. Pass 'attachment' for a Download link, which asks the
 * same route for the save-to-disk presentation of the same bytes.
 *
 * The browser sends the HttpOnly access cookie (set at login / refresh,
 * scoped to /api/v1/) along with the GET request, which the backend
 * AuthMiddleware accepts as a fallback when no Authorization: Bearer
 * header is present — so a plain <a href> link click authenticates
 * without the SPA having to intercept the navigation. The backend redirects
 * to the source URL for URL-ingested docs, or streams GCS bytes for
 * uploaded files.
 */
export function getDocumentContentURL(
  content: ContentRef,
  disposition: 'inline' | 'attachment' = 'inline',
): string {
  const url = new URL(content.href, new URL(apiBaseUrl(), window.location.origin));
  if (disposition === 'attachment') url.searchParams.set('disposition', disposition);
  return url.toString();
}

/**
 * List documents. Wire format is NDJSON; the helper unwraps that into a flat
 * array plus the total-count header.
 *
 * `statuses` narrows the collection, and for `pending_approval` / `quarantined`
 * it also WIDENS the row set for a caller who may approve — which is what the
 * retired `GET /documents/pending` route existed to do. The server refuses a
 * status token it cannot spell (422/400) rather than silently dropping the
 * filter, so a typo is an error, never someone's whole document list.
 */
export async function listDocuments(
  limit = 50,
  offset = 0,
  statuses?: DocumentStatus[]
): Promise<DocumentListResult> {
  logger.debug('Listing documents', { limit, offset, statuses });
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (statuses && statuses.length > 0) params.set('status', statuses.join(','));
  const { items, totalCount } = await api.getNDJSON<Document>(`/documents?${params.toString()}`);
  logger.info('Documents listed', { count: items.length, total: totalCount });
  return { documents: items, totalCount };
}

/**
 * Get document details
 */
export async function getDocument(id: string): Promise<Document> {
  logger.debug('Getting document', { id });
  const response = await api.get<Document>(`/documents/${id}`);
  logger.info('Document retrieved', { id, status: response.status });
  return response;
}

/**
 * Get document processing status
 */
export async function getDocumentStatus(id: string): Promise<DocumentStatusResponse> {
  const response = await api.get<DocumentStatusResponse>(`/documents/${id}/status`);
  logger.debug('Document status checked', { id, status: response.status });
  return response;
}

/**
 * Get a document's ordered, structure-aware chunks — the document's
 * "components" for the Protocol Studio's left pane (CUJ-102). Behind the same
 * ACL chain as the other document {id} reads.
 */
export async function getDocumentChunks(id: string): Promise<DocumentChunksResponse> {
  const response = await api.getPage<DocumentChunksResponse>(`/documents/${id}/chunks`);
  logger.debug('Document chunks retrieved', { id, count: response.items.length });
  return response;
}


/**
 * One immutable statement of what a document IS at a point in time: its content
 * plus the metadata describing it. Mirrors the API's DocumentVersion.
 */
export interface DocumentVersion {
  id: string;
  version_no: number;
  content_id: string;
  title: string;
  filename?: string;
  category: string;
  provenance: string;
  custom_metadata?: Record<string, string>;
  requires_approval: boolean;
  status: DocumentStatus;
  error_message?: string;
  approved_by?: string;
  approved_at?: string;
  created_by: string;
  created_at: string;
}


export interface CreateDocumentVersionResponse {
  document_id: string;
  status: DocumentStatus;
}

/**
 * Re-derive a document from its bytes: POST /documents/{id}/versions.
 *
 * This is the route `POST /documents/{id}/reprocess` used to be. A
 * re-derivation is a new statement about the document rather than an edit of
 * the old one, so it posts to the versions collection.
 *
 * Owner/editor only (enforced server-side by the docEditor middleware).
 * Re-derivation is idempotent — the document's chunks are replaced, not
 * appended.
 *
 * The Idempotency-Key is generated once per call and travels in `options`, so
 * the api-client's own cold-start retry layer (503 / empty-200, which re-runs
 * the request with the SAME options) replays the cached result instead of
 * starting a second derivation. It does NOT dedupe a user re-clicking Retry or
 * a full page reload — each is a fresh call with a new key; the real guard
 * against a double run there is the server's conditional UPDATE (the row can
 * only leave the failed state once). The response is a 202 carrying the new
 * status ("processing") — the derivation itself runs out of process, so there
 * is no version to name yet. Callers update local state from this body rather
 * than re-reading (the eventual-consistency rule: trust the mutation response).
 *
 * Throws ApiError with code 'INVALID_STATE' (409) when the document is not in a
 * state that can be re-derived (e.g. already ready or still processing), and
 * 'NOT_FOUND' (404) when it no longer exists.
 */
export async function createDocumentVersion(id: string): Promise<CreateDocumentVersionResponse> {
  const idempotencyKey = randomUUID();
  logger.info('Creating document version', { id });
  const response = await api.post<CreateDocumentVersionResponse>(
    `/documents/${id}/versions`,
    undefined, // handler reads no request body
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  logger.info('Document re-derivation queued', { id, status: response.status });
  return response;
}

/** A document's versions, newest first: GET /documents/{id}/versions. */
export async function listDocumentVersions(id: string): Promise<Page<DocumentVersion>> {
  return await api.getPage<Page<DocumentVersion>>(`/documents/${id}/versions`);
}
